<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { Album } from "@/models"
import type { IMenuItem } from "@/types"
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import cover from "@brand-cover"
import { computed, onMounted, ref, useTemplateRef } from "vue"
import { AppLink, DropdownMenu, Icon, Tooltip } from "@/components/ui"
import { useTranslation } from "@/composables"
import { AlbumPrivacyOptions } from "@/models"

interface AlbumCardProps {
  album?: Album
  isLoading?: boolean
  isDraggable?: boolean
  location?: number
  isFlashing?: boolean
  canMoveToStart?: boolean
  canMoveToEnd?: boolean
}

type ItemState = { type: "idle" } | { type: "dragging" }

const props = withDefaults(defineProps<AlbumCardProps>(), { album: undefined, isLoading: false, isDraggable: false, isFlashing: false })
const emit = defineEmits<{
  showLinks: [album?: Album]
  showExport: [album?: Album]
  move: [albumId: number, location: number]
  moveToStart: [album?: Album]
  moveToEnd: [album?: Album]
}>()

const idleState: ItemState = { type: "idle" }
const draggingState: ItemState = { type: "dragging" }

const dragState = ref<ItemState>(idleState)
const isDraggedOver = ref<boolean>(false)

const { t, d } = useTranslation()

const privacyIconMap: Record<AlbumPrivacyOptions, IconKeys> = {
  [AlbumPrivacyOptions.Public]: "EyeOn",
  [AlbumPrivacyOptions.Private]: "EyeOff",
  [AlbumPrivacyOptions.Protected]: "Lock",
}

const privacyLabel = computed(() => {
  const privacy = props.album?.getFormattedPrivacy()

  switch (privacy) {
    case AlbumPrivacyOptions.Private:
      return t("album_card.privacy_private")
    case AlbumPrivacyOptions.Protected:
      return t("album_card.privacy_protected")
    default:
      return t("album_card.privacy_public")
  }
})

const cardRef = useTemplateRef("cardRef")

const moveToMenuItems = computed<IMenuItem[]>(() => {
  const moveUp = (albumId?: number, location?: number) => {
    if (!albumId || !location) {
      return
    }

    emit("move", albumId, location - 1)
  }

  const moveDown = (albumId?: number, location?: number) => {
    if (!albumId || !location) {
      return
    }

    emit("move", albumId, location + 1)
  }

  return [
    { key: "move-to-start", label: t("album_card.move_menu.start"), onClick: () => emit("moveToStart", props.album), dismissOnInteract: true, isHidden: !props.canMoveToStart },
    { key: "move-up", label: t("album_card.move_menu.up"), onClick: () => moveUp(props.album?.id, props.location), dismissOnInteract: true, isHidden: !props.canMoveToStart },
    { key: "move-down", label: t("album_card.move_menu.down"), onClick: () => moveDown(props.album?.id, props.location), dismissOnInteract: true, isHidden: !props.canMoveToEnd },
    { key: "move-to-end", label: t("album_card.move_menu.end"), onClick: () => emit("moveToEnd", props.album), dismissOnInteract: true, isHidden: !props.canMoveToEnd },
  ]
})

onMounted(() => {
  if (props.isDraggable && cardRef.value) {
    draggable({
      element: cardRef.value as HTMLElement,
      getInitialData: () => ({ album: props.album?.id, location: props.location }),
      canDrag: () => true,
      onDragStart: () => dragState.value = draggingState,
      onDrop: () => {
        dragState.value = idleState
        isDraggedOver.value = false
      },
    })

    dropTargetForElements({
      element: cardRef.value,
      canDrop({ source }) {
        if (source.data?.album === props.album?.id) {
          return false
        }

        return true
      },
      getData: ({ source }) => {
        return { album: source.data?.album, location: props.location }
      },
      onDragEnter: () => {
        isDraggedOver.value = true
      },
      onDragLeave: () => isDraggedOver.value = false,
      onDrop: () => {
        dragState.value = idleState
        isDraggedOver.value = false
      },
    })
  }
})
</script>

<template>
  <div ref="cardRef" class="border rounded-xl" :class="[isDraggable && isDraggedOver ? 'border-primary' : 'border-transparent']" @contextmenu.prevent>
    <div class="w-full bg-neutral-100 rounded-xl font-comfortaa overflow-clip relative" :class="{ 'opacity-60': dragState.type === draggingState.type, 'album-flash': isFlashing }">
      <div v-if="isLoading" class="gpx-skeleton w-full min-h-32" />
      <div v-else class="flex">
        <div class="w-full min-h-32 flex justify-between h-full">
          <div class="flex p-2 md:p-4">
            <div v-if="isDraggable" class="h-full flex items-center justify-center mr-5">
              <DropdownMenu :items="moveToMenuItems" :disabled="moveToMenuItems.length === 0">
                <template #activator="{ isOpen }">
                  <button
                    class="rounded-xl p-2 cursor-grab transition-all duration-300 ease-in-out"
                    type="button"
                    :class="[isOpen ? 'bg-link text-white' : 'bg-white']"
                  >
                    <Icon icon="GripVertical" />
                  </button>
                </template>
              </DropdownMenu>
            </div>
            <div class="flex flex-col">
              <div class="flex gap-3 items-center">
                <AppLink
                  :to="`/guest/${album?.eventVaultId}/welcome?album=${album?.id}`" target="_blank"
                  class="no-underline text-inherit"
                >
                  <h2 data-test="album-title" class="text-lg font-semibold">
                    {{ album?.title }}
                  </h2>
                </AppLink>
                <Tooltip>
                  <Icon
                    :icon="privacyIconMap[(album?.getFormattedPrivacy()) ?? AlbumPrivacyOptions.Public]"
                    class="w-fit mr-1" size="sm"
                  />
                  <template #content>
                    {{ privacyLabel }}
                  </template>
                </Tooltip>
              </div>

              <div v-if="album?.date" data-test="album-date" class="text-sm mb-auto">
                {{ d(album.date, "short") }}
              </div>

              <div class="flex gap-5 items-center">
                <button
                  data-test="show-links-btn"
                  class="flex gap-2 items-center text-sm text-link hover:text-link-hover font-comfortaa font-bold hover:underline focus:underline cursor-pointer"
                  @click.prevent="emit('showLinks', album)"
                >
                  <Icon icon="Link" />
                  <span class="sr-only md:not-sr-only">{{ t("album_card.view_link") }}</span>
                </button>
                <button
                  data-test="show-export-btn"
                  class="flex gap-2 items-center text-sm text-link hover:text-link-hover font-comfortaa font-bold hover:underline focus:underline cursor-pointer"
                  @click="emit('showExport', album)"
                >
                  <Icon icon="FolderDownload" />
                  <span class="sr-only md:not-sr-only">{{ t("album_card.generate_download") }}</span>
                </button>
                <AppLink
                  :to="`/event/${album?.eventVaultId}/album/${album?.id}/edit`"
                  class="flex gap-2 items-center text-sm text-link hover:text-link-hover font-comfortaa font-bold hover:underline focus:underline cursor-pointer"
                >
                  <Icon icon="FolderEdit" />
                  <span class="sr-only md:not-sr-only">{{ t("album_card.edit") }}</span>
                </AppLink>
              </div>
            </div>
          </div>

          <div class="relative">
            <div class="absolute top-0 left-0 w-1/2 h-full bg-linear-to-r from-neutral-100 via-neutral-100/65 via-65% to-transparent" />
            <img
              v-if="album?.coverImageUrl"
              :src="album.coverImageUrl"
              :alt="t('album_card.img_alt')"
              class="max-h-32 h-full md:w-72 object-cover touch-callout-none"
            >
            <img
              v-else
              :src="cover"
              :alt="t('album_card.img_alt')"
              class="max-h-32 h-full md:w-72 object-cover"
              @contextmenu.prevent
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.touch-callout-none {
  -webkit-touch-callout: none;
  user-select: none;
}

@keyframes flash {
  0% {
    opacity: 0;
  }
  30% {
    opacity: 0.3;
  }
  100% {
    opacity: 0;
  }
}

.album-flash::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-link);
  border-radius: inherit;
  pointer-events: none;
  z-index: 10;
  animation: flash 0.6s ease-out;
}
</style>
